import { useSyncExternalStore } from 'react'
import { onEdgeEvent } from '../servers/commandStream'
import type { BackupJob } from '../transport/api'

/**
 * WHERE EACH AGENT'S BACKUP IS UP TO — one store, fed by the machines themselves.
 *
 * A backup is a job on the agent's MACHINE (edge agentBackup.ts), which announces every
 * step as a `backup_progress` edge event. Kept here, at module level and not in the
 * card, so closing the agent's card and opening it again — or opening it from another
 * place — still shows the backup that is running, with no request to ask.
 *
 * Events are the fast lane, never the only one: the BFF drops them under backpressure,
 * so the card showing a running job re-asks its machine when the account stream
 * reopens and when the app comes back into view (BackupForm) — never on a clock — and
 * whatever arrives, from either lane, lands here.
 */

const jobs = new Map<string, BackupJob>()
const listeners = new Set<() => void>()
const finished = new Set<() => void>()

const jobKey = (device: string, session: string) => `${device}:${session}`
export const isOver = (j: BackupJob | null | undefined) => j?.phase === 'done' || j?.phase === 'failed'

/**
 * Record what a machine said about a job. Never backwards: an older job's late event
 * cannot replace a newer job, and a finished job cannot be un-finished by a progress
 * event that was overtaken on the way.
 */
export function noteJob(device: string, job: BackupJob): void {
  const k = jobKey(device, job.session)
  const prev = jobs.get(k)
  if (prev && prev.job_id !== job.job_id && prev.started_at > job.started_at) return
  if (prev && prev.job_id === job.job_id && isOver(prev) && !isOver(job)) return
  if (prev && prev.job_id === job.job_id && JSON.stringify(prev) === JSON.stringify(job)) return
  jobs.set(k, job)
  for (const l of [...listeners]) l()
  if (job.phase === 'done' && !(prev?.job_id === job.job_id && prev.phase === 'done')) {
    for (const f of [...finished]) f()
  }
}

function getJob(device: string, session: string): BackupJob | null {
  return jobs.get(jobKey(device, session)) ?? null
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function useBackupJob(device: string, session: string): BackupJob | null {
  return useSyncExternalStore(subscribe, () => getJob(device, session))
}

/** Be told when any backup finishes — the Backups list re-reads on it. */
export function onBackupFinished(cb: () => void): () => void {
  finished.add(cb)
  return () => finished.delete(cb)
}

onEdgeEvent((event, data) => {
  if (event !== 'backup_progress') return
  const job = data['job'] as BackupJob | undefined
  const device = typeof data['device_id'] === 'string' ? data['device_id'] : ''
  if (job && typeof job.job_id === 'string' && typeof job.session === 'string' && device) noteJob(device, job)
})

/** Tests only. */
export function __resetBackupJobs(): void {
  jobs.clear()
}
